import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { MasterSolutionDetailModel } from 'src/app/models/preliminaries/master-solution-detail.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { MasterSolutionDetailService } from 'src/app/services/preliminaries/master-solution-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-master-solution-detail',
  templateUrl: './register-master-solution-detail.component.html',
  styleUrls: ['./register-master-solution-detail.component.scss'],
  standalone: false
})
export class RegisterMasterSolutionDetailComponent implements OnChanges {

  @Input() masterSolutionDetailContract: MasterSolutionDetailModel = new MasterSolutionDetailModel();
  @Input() masterSolutionDetails: MasterSolutionDetailModel[] = [];
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() updateListDetail = new EventEmitter<boolean>();


  formMasterSolutionDetail: FormGroup = new FormGroup({});
  masterSolutionDetailId: number = 0;
  codeNameArticle: string = '';

  constructor(private articleService: ArticleService,
    private formBuilder: FormBuilder,
    private toastHandlerService: ToastHandlerService,
    private masterSolutionDetailService: MasterSolutionDetailService) {
    this.validateForm(new MasterSolutionDetailModel());
  }

  async ngOnChanges(changes: SimpleChanges) {
    const masterSolutionDetails = changes["masterSolutionDetails"];
    const masterSolutionDetailContract = changes['masterSolutionDetailContract'];
    if (masterSolutionDetails && !masterSolutionDetails.firstChange) {
      await this.createDetails(this.masterSolutionDetails);
      return;
    }
    if (masterSolutionDetailContract && !masterSolutionDetailContract.firstChange) {
      if (this.masterSolutionDetailContract.masterSolutionDetailId == 0) {
        this.createNewDetail();
      } else {
        this.masterSolutionDetailId = this.masterSolutionDetailContract.masterSolutionDetailId;
        this.validateForm(this.masterSolutionDetailContract);
      }
    }
  }

  async clearForm() {
    this.codeNameArticle = '';
    this.isFormView.emit(false);
    await this.validateForm(new MasterSolutionDetailModel())
  }

  async createDetails(masterSolutionDetails: MasterSolutionDetailModel[]) {
    try {
      await firstValueFrom(this.masterSolutionDetailService.createDetails(masterSolutionDetails));
      this.toastHandlerService.showToast('success', 'Los detalles de la solución fueron creados exitosamente.', '');
      this.updateListDetail.emit(true);
    } catch (error) {
      console.error('Error al crear los detalles de la solución de forma multiple.', error);
    }
  }

  async getArticle(articleId: number | null) {
    try {
      if (articleId != null && articleId != 0) {
        const article = await firstValueFrom(this.articleService.getArticleById(articleId));
        this.codeNameArticle = `${article.code} - ${article.name}`;
      } else {
        this.codeNameArticle = '';
      }
    } catch (error) {
      this.codeNameArticle = '';
      console.error('Error al obtener el articulo.', error);
    }
  }

  async createNewDetail() {
    this.masterSolutionDetailId = 0;
    this.validateForm(this.masterSolutionDetailContract);
  }

  async send() {
    this.validateFormControls();
    const quantity = this.formMasterSolutionDetail.get("quantity")?.value;
    if (quantity < 0 || quantity == 0 || !quantity) {
      this.toastHandlerService.showToast('warning', 'La cantidad no puede ser menor a uno.');
      return;
    }
    if (this.formMasterSolutionDetail.valid) {
      const modelMasterSolutionDetail = this.formMasterSolutionDetail.getRawValue() as MasterSolutionDetailModel;
      if (this.masterSolutionDetailId == 0) {
        try {
          await firstValueFrom(this.masterSolutionDetailService.createAsync(modelMasterSolutionDetail));
          this.masterSolutionDetailContract = new MasterSolutionDetailModel();
          this.updateListDetail.emit(true);
          this.validateForm(this.masterSolutionDetailContract);
          this.toastHandlerService.showToast('success', 'El detalle de la solución fue creado exitosamente.');
        } catch (error) {
          console.error('Error al crear el detalle de la solución.', error);
        }
      } else {
        this.masterSolutionDetailContract.quantity = modelMasterSolutionDetail.quantity;
        try {
          await firstValueFrom(this.masterSolutionDetailService.updateAsync(this.masterSolutionDetailContract));
          this.masterSolutionDetailContract = new MasterSolutionDetailModel();
          this.updateListDetail.emit(true);
          this.validateForm(this.masterSolutionDetailContract);
          this.toastHandlerService.showToast('success', 'El detalle de la solución fue modificado exitosamente.');
        } catch (error) {
          console.error('Error al modificado el detalle de la solución.', error);
        }
      }
    } else {
      this.showValidationMessagesDetail();
    }
  }

  showValidationMessagesDetail() {
    const controlMessages: Record<string, Record<string, string>> = {
      masterSolutionId: {
        required: 'El campo Solución es obligatorio.',
        min: 'Debes seleccionar una Solución válida.'
      },
      articleId: {
        required: 'El campo Artículo es obligatorio.',
        min: 'Debes seleccionar un Artículo válido.'
      },
      quantity: {
        required: 'El campo Cantidad es obligatorio.'
      }
    };
    Object.keys(controlMessages).forEach(control => {
      const formControl = this.formMasterSolutionDetail.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  async validateForm(masterSolutionDetail: MasterSolutionDetailModel) {
    this.formMasterSolutionDetail = this.formBuilder.group({
      masterSolutionId: [masterSolutionDetail.masterSolutionId, [Validators.required, Validators.min(1)]],
      articleId: [masterSolutionDetail.articleId, [Validators.required, Validators.min(1)]],
      quantity: [masterSolutionDetail.quantity, [Validators.required]]
    });
    if (this.masterSolutionDetailContract.articleId > 0) {
      await this.getArticle(masterSolutionDetail.articleId);
    }
  }

  validateFormControls() {
    Object.keys(this.formMasterSolutionDetail.controls).forEach((key) => {
      const control = this.formMasterSolutionDetail.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
